import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { hash } from "@ember/helper";
import { on } from "@ember/modifier";
import ComboBox from "discourse/select-kit/components/combo-box";
import MultiSelect from "discourse/select-kit/components/multi-select";
import DButton from "discourse/ui-kit/d-button";
import I18n, { i18n } from "discourse-i18n";

export default class ExternalControlledGroups extends Component {
  @service siteSettings;
  @tracked userFlagsArray = [];

  constructor(...args) {
    super(...args);
    const currentUserFlags =
      this.args.outletArgs?.group?.custom_fields?.user_flags;
    this.userFlagsArray = currentUserFlags
      ? currentUserFlags.split("|").filter(Boolean)
      : [];
  }

  get group() {
    return this.args.outletArgs?.group;
  }

  get customFields() {
    return this.group?.custom_fields || {};
  }

  get externalProviders() {
    const externalControlledGroups =
      I18n.translations[I18n.locale]?.js?.waze?.external_controlled_groups ??
      I18n.translations.en?.js?.waze?.external_controlled_groups ??
      {};
    const providerNames = externalControlledGroups.provider || {};
    return Object.keys(providerNames).map((id) => ({
      id,
      name:
        i18n(`waze.external_controlled_groups.provider.${id}`) ||
        providerNames[id],
    }));
  }

  get userFlagOptions() {
    return (this.siteSettings.waze_external_controlled_user_flag || "")
      .split("|")
      .filter(Boolean);
  }

  get welcomeEmailTemplateNameOptions() {
    return (
      this.siteSettings.waze_external_controlled_welcome_email_template_name ||
      ""
    )
      .split("|")
      .filter(Boolean)
      .map((templateName) => ({
        id: templateName,
        name: templateName,
      }));
  }

  get groupCategories() {
    const externalControlledGroups =
      I18n.translations[I18n.locale]?.js?.waze?.external_controlled_groups ??
      I18n.translations.en?.js?.waze?.external_controlled_groups ??
      {};
    const categoryNames = externalControlledGroups.category || {};
    return Object.keys(categoryNames).map((id) => ({
      id,
      name:
        i18n(`waze.external_controlled_groups.category.${id}`) ||
        categoryNames[id],
    }));
  }

  @action
  changeUserFlags(newFlags) {
    this.userFlagsArray = newFlags;
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      user_flags: newFlags.join("|"),
    });
  }

  @action
  resetWelcomeEmail() {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      welcome_email_template_name: "",
    });
  }

  @action
  resetGroupCategory() {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      group_category: "",
    });
  }

  @action
  changeExternalProviderId(value) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      external_provider_id: value,
    });
  }

  @action
  changeGroupCategory(value) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      group_category: value,
    });
  }

  @action
  changeWelcomeEmailTemplateName(value) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      welcome_email_template_name: value,
    });
  }

  @action
  toggleEnableWazeExternalControlled(event) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      enable_waze_external_controlled: event.target.checked,
    });
  }

  @action
  toggleEnableTestflightMembershipBinding(event) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      enable_testflight_membership_binding: event.target.checked,
    });
  }

  @action
  changeWelcomeTopic(event) {
    this.group?.set("custom_fields", {
      ...this.group?.custom_fields,
      welcome_topic: event.target.value,
    });
  }

  <template>
    <h3>{{i18n "waze.external_controlled_groups.settings_title"}}</h3>
    <section class="field">
      <div class="enable-waze-external-controlled-groups">
        <label class="checkbox-label">
          <input
            type="checkbox"
            checked={{this.customFields.enable_waze_external_controlled}}
            {{on "change" this.toggleEnableWazeExternalControlled}}
          />
          {{i18n
            "waze.external_controlled_groups.membership_controlled_by_external"
          }}
        </label>
      </div>
    </section>
    {{#if this.customFields.enable_waze_external_controlled}}
      <section class="field">
        <label for="waze-external-controlled-groups-new-topic-button-label">
          {{i18n "waze.external_controlled_groups.external_provider"}}
        </label>
        <ComboBox
          @id="waze-external-controlled-groups-new-topic-button-label"
          @class="input-xxlarge"
          @content={{this.externalProviders}}
          @value={{this.customFields.external_provider_id}}
          @onChange={{this.changeExternalProviderId}}
          @options={{hash allowAny=false}}
        />
      </section>
      <br />
      <section class="field">
        <label for="waze-external-controlled-groups-category">
          {{i18n "waze.external_controlled_groups.group_category"}}
        </label>
        <ComboBox
          @id="waze-external-controlled-groups-category"
          @class="input-xxlarge"
          @content={{this.groupCategories}}
          @value={{this.customFields.group_category}}
          @onChange={{this.changeGroupCategory}}
          @options={{hash allowAny=false}}
        />
        {{#if this.customFields.group_category}}
          <DButton
            @class="btn-default undo"
            @action={{this.resetGroupCategory}}
            @icon="undo"
            @label="admin.settings.reset"
          />
        {{/if}}
      </section>
      <br />
      <section class="field">
        <label for="waze-group-welcome-topic">
          {{i18n "waze.external_controlled_groups.welcome_topic"}}
        </label>
        <input
          type="text"
          value={{this.customFields.welcome_topic}}
          id="waze-group-welcome-topic"
          class="input-xxlarge"
          placeholder={{i18n
            "waze.external_controlled_groups.welcome_topic_placeholder"
          }}
          {{on "input" this.changeWelcomeTopic}}
        />
      </section>
      <br />
      <section class="field">
        <label for="waze-external-controlled-groups-user-flags">
          {{i18n "waze.external_controlled_groups.user_flags_description"}}
        </label>
        <label>
          {{i18n "waze.external_controlled_groups.user_flags_notice"}}
        </label>
        <MultiSelect
          @id="waze-external-controlled-groups-user-flags"
          @content={{this.userFlagOptions}}
          @valueProperty={{null}}
          @nameProperty={{null}}
          @value={{this.userFlagsArray}}
          @onChange={{this.changeUserFlags}}
        />
      </section>
      <br />
      <section class="field">
        <div class="enable_testflight_membership_binding">
          <label class="checkbox-label">
            <input
              type="checkbox"
              checked={{this.customFields.enable_testflight_membership_binding}}
              {{on "change" this.toggleEnableTestflightMembershipBinding}}
            />
            {{i18n
              "waze.external_controlled_groups.testflight_membership_binding"
            }}
          </label>
        </div>
      </section>
      <br />
      <section class="field">
        <label for="waze-external-controlled-welcome-email">
          {{i18n "waze.external_controlled_groups.welcome_email_template"}}
        </label>
        <ComboBox
          @id="waze-external-controlled-welcome-email"
          @class="input-xxlarge"
          @content={{this.welcomeEmailTemplateNameOptions}}
          @value={{this.customFields.welcome_email_template_name}}
          @onChange={{this.changeWelcomeEmailTemplateName}}
          @options={{hash allowAny=false}}
        />
        {{#if this.customFields.welcome_email_template_name}}
          <DButton
            @class="btn-default undo"
            @action={{this.resetWelcomeEmail}}
            @icon="undo"
            @label="admin.settings.reset"
          />
        {{/if}}
      </section>
    {{/if}}
  </template>
}
